// Öffentlicher Player /player/<slug> (ohne Anmeldung) und Widgets (?widget=player|bar|pill|history|schedule|requests).
// Eigene Gestaltung über Einstellungen › Öffentliche Seite.

import { useEffect, useRef, useState } from "react";

const DAYS = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];
const hhmm = (m: number) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;

function useJson<T = any>(url: string | null, every = 0): T | null {
  const [d, setD] = useState<T | null>(null);
  useEffect(() => {
    if (!url) return;
    let alive = true;
    const load = () => fetch(url).then((r) => (r.ok ? r.json() : null)).then((j) => alive && setD(j)).catch(() => {});
    load();
    const t = every ? window.setInterval(() => document.visibilityState === "visible" && load(), every) : undefined;
    return () => {
      alive = false;
      if (t) window.clearInterval(t);
    };
  }, [url, every]);
  return d;
}

export function PublicPlayer({ slug, widget }: { slug: string; widget: string | null }) {
  const preview = new URLSearchParams(window.location.search).get("preview");
  const info = useJson<any>(`/api/public/page/${slug}${preview ? `?preview=${preview}` : ""}`);
  const now = useJson<any>(`/api/v1/stations/${slug}/now`, 10_000);
  const blocks: any[] = info?.page?.layout?.blocks ?? [];
  const history = useJson<any[]>(info?.page?.sections?.includes("history") || widget === "history" || blocks.some((b) => b.type === "history") ? `/api/v1/stations/${slug}/history?limit=20` : null, 30_000);
  const schedule = useJson<any>(info?.page?.schedule_view !== "off" ? `/api/v1/stations/${slug}/schedule` : null);
  const greetings = useJson<any[]>(info?.page?.sections?.includes("requests") || blocks.some((b) => b.type === "greetings") ? `/api/public/greetings/${slug}` : null, 60_000);
  const audio = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [sent, setSent] = useState<string | null>(null);
  const [req, setReq] = useState({ kind: "request", name: "", song: "", message: "" });
  if (!info) return <div style={{ padding: 20, fontFamily: "sans-serif" }}>…</div>;
  const p = info.page;
  const FONTS: Record<string, string> = { system: "Inter, system-ui, sans-serif", serif: "Georgia, 'Times New Roman', serif", rounded: "ui-rounded, 'SF Pro Rounded', 'Nunito', system-ui, sans-serif", mono: "ui-monospace, 'SF Mono', Menlo, monospace", condensed: "'Arial Narrow', 'Roboto Condensed', system-ui, sans-serif" };
  const css: React.CSSProperties = { background: p.background, color: "#fff", minHeight: widget ? undefined : "100vh", fontFamily: FONTS[p.font] ?? FONTS.system };
  const toggle = () => {
    const a = audio.current;
    if (!a || !info.listen) return;
    if (playing) { a.pause(); a.removeAttribute("src"); a.load(); setPlaying(false); }
    else { a.src = `${info.listen}${info.listen.includes("?") ? "&" : "?"}t=${Date.now()}`; a.play().then(() => setPlaying(true)).catch(() => setPlaying(false)); }
  };
  const cur = now?.current;
  const title = cur ? (cur.kind === "live" ? cur.title : `${cur.artist ? `${cur.artist} – ` : ""}${cur.title}`) : p.tagline || info.station.name;
  const playBtn = (
    <button onClick={toggle} disabled={!info.listen} style={{ width: 56, height: 56, borderRadius: "50%", border: 0, background: p.accent, color: "#fff", fontSize: 22, cursor: "pointer", flex: "none" }}>{playing ? "■" : "▶"}</button>
  );
  const sendReq = async () => {
    const r = await fetch(`/api/public/requests/${slug}`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(req) });
    const d = await r.json().catch(() => ({}));
    setSent(r.ok ? "Danke! Wir haben es bekommen." : d.error ?? "Das hat nicht geklappt.");
    if (r.ok) setReq({ kind: req.kind, name: req.name, song: "", message: "" });
  };
  const box: React.CSSProperties = { background: "rgba(255,255,255,0.07)", borderRadius: 12, padding: 16, marginBottom: 14 };
  const input: React.CSSProperties = { width: "100%", padding: 8, borderRadius: 8, border: "1px solid rgba(255,255,255,.2)", background: "rgba(0,0,0,.2)", color: "#fff", marginBottom: 6 };

  const requestsForm = (
    <div style={box}>
      <h3 style={{ marginTop: 0 }}>Wunsch oder Gruß</h3>
      <div style={{ display: "flex", gap: 8, marginBottom: 6 }}>
        {[["request", "Musikwunsch"], ["greeting", "Gruß"]].map(([k, l]) => <button key={k} onClick={() => setReq({ ...req, kind: k })} style={{ flex: 1, padding: 6, borderRadius: 8, border: 0, background: req.kind === k ? p.accent : "rgba(255,255,255,.12)", color: "#fff" }}>{l}</button>)}
      </div>
      <input style={input} placeholder="Dein Name" value={req.name} onChange={(e) => setReq({ ...req, name: e.target.value })} />
      {req.kind === "request" && <input style={input} placeholder="Interpret – Titel" value={req.song} onChange={(e) => setReq({ ...req, song: e.target.value })} />}
      <textarea style={{ ...input, minHeight: 60 }} placeholder="Nachricht" value={req.message} onChange={(e) => setReq({ ...req, message: e.target.value })} />
      <button onClick={sendReq} style={{ padding: "8px 14px", borderRadius: 8, border: 0, background: p.accent, color: "#fff" }}>Senden</button>
      {sent && <div style={{ marginTop: 6, opacity: 0.85 }}>{sent}</div>}
    </div>
  );

  if (widget === "pill") return <div style={{ ...css, display: "flex", alignItems: "center", gap: 10, padding: 4, borderRadius: 30 }}>{playBtn}<span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{title}</span><audio ref={audio} /></div>;
  if (widget === "bar") return <div style={{ ...css, display: "flex", alignItems: "center", gap: 12, padding: 8 }}>{playBtn}<div style={{ minWidth: 0 }}><b>{info.station.name}</b><div style={{ opacity: 0.8, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{title}</div></div><audio ref={audio} /></div>;
  if (widget === "history") return <div style={{ ...css, padding: 12 }}>{(history ?? []).map((h, i) => <div key={i} style={{ padding: "4px 0", borderBottom: "1px solid rgba(255,255,255,.1)" }}><span style={{ opacity: 0.6 }}>{new Date(h.started_at).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}</span> {h.artist} – {h.title}</div>)}</div>;
  if (widget === "requests") return <div style={{ ...css, padding: 12 }}>{requestsForm}</div>;

  const scheduleBox = schedule?.programs?.length ? (
    <div style={box}>
      <h3 style={{ marginTop: 0 }}>Programm</h3>
      {schedule.programs.map((s: any, i: number) => <div key={i} style={{ padding: "4px 0" }}><b>{hhmm(s.start_min)}</b> {s.name}{s.host ? ` · ${s.host}` : ""} <span style={{ opacity: 0.6 }}>{s.date ? String(s.date).slice(0, 10) : (s.days ?? []).map((d: number) => DAYS[d]).join(" ")}</span></div>)}
    </div>
  ) : null;
  if (widget === "schedule") return <div style={{ ...css, padding: 12 }}>{scheduleBox}</div>;

  const nowBox = (
    <div style={{ ...box, display: "flex", alignItems: "center", gap: 16 }}>
      {playBtn}
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 1, color: now?.on_air === "live" ? "#ff8a8a" : p.accent }}>{now?.on_air === "live" ? "● Live" : "Jetzt läuft"}</div>
        <div style={{ fontSize: 20, fontWeight: 700 }}>{title}</div>
        {now?.next?.length > 0 && <div style={{ opacity: 0.7, fontSize: 13 }}>Danach: {now.next[0].artist ? `${now.next[0].artist} – ` : ""}{now.next[0].title}</div>}
      </div>
    </div>
  );
  // Seitenbau: blocks in order (when the page has a layout).
  if (!widget && blocks.length) {
    const block = (b: any) => {
      const pr = b.props ?? {};
      switch (b.type) {
        case "hero": return (
          <div style={{ padding: "40px 0 20px", textAlign: pr.align === "left" ? "left" : "center", backgroundImage: pr.image ? `linear-gradient(rgba(0,0,0,.45),rgba(0,0,0,.45)),url(${pr.image})` : undefined, backgroundSize: "cover", borderRadius: 14, marginBottom: 14 }}>
            <h1 style={{ margin: 0, fontSize: 38 }}>{pr.title || p.title || info.station.name}</h1>
            <div style={{ opacity: 0.8, fontSize: 18 }}>{pr.subtitle ?? p.tagline}</div>
          </div>
        );
        case "player": case "now": return nowBox;
        case "history": return history ? <div style={box}><h3 style={{ marginTop: 0 }}>{pr.heading || "Zuletzt gespielt"}</h3>{history.slice(0, Number(pr.count) || 10).map((h, i) => <div key={i} style={{ padding: "3px 0" }}><span style={{ opacity: 0.6 }}>{new Date(h.started_at).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}</span> {h.artist} – {h.title}</div>)}</div> : null;
        case "schedule": return scheduleBox;
        case "requests": return p.requests_enabled ? requestsForm : null;
        case "greetings": return greetings?.length ? <div style={box}><h3 style={{ marginTop: 0 }}>{pr.heading || "Grüße"}</h3>{greetings.map((g, i) => <div key={i} style={{ padding: "4px 0" }}><b>{g.name || "Jemand"}</b>: {g.message}{g.song ? ` 🎵 ${g.song}` : ""}</div>)}</div> : null;
        case "hosts": return info.hosts?.length ? (
          <div style={box}><h3 style={{ marginTop: 0 }}>{pr.heading || "Unser Team"}</h3>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(150px,1fr))", gap: 12 }}>
              {info.hosts.map((h: any) => (
                <div key={h.id} style={{ textAlign: "center" }}>
                  {h.has_photo && <img src={`/api/public/host-photo/${h.id}`} alt={h.name} style={{ width: 96, height: 96, borderRadius: "50%", objectFit: "cover" }} />}
                  <div style={{ fontWeight: 700 }}>{h.name}</div>
                  {h.slogan && <div style={{ fontSize: 13, opacity: 0.75 }}>{h.slogan}</div>}
                </div>
              ))}
            </div>
          </div>
        ) : null;
        case "text": return <div style={box}>{pr.heading && <h3 style={{ marginTop: 0 }}>{pr.heading}</h3>}{String(pr.body ?? "").split(/\n{2,}/).map((para: string, i: number) => <p key={i} style={{ margin: "0 0 10px", whiteSpace: "pre-wrap" }}>{para}</p>)}</div>;
        case "image": return pr.src ? <div style={{ marginBottom: 14 }}>{pr.link ? <a href={pr.link} target="_blank" rel="noreferrer"><img src={pr.src} alt={pr.alt ?? ""} style={{ width: "100%", borderRadius: 12 }} /></a> : <img src={pr.src} alt={pr.alt ?? ""} style={{ width: "100%", borderRadius: 12 }} />}</div> : null;
        case "buttons": return (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 14, justifyContent: "center" }}>
            {String(pr.items ?? "").split("\n").map((l: string) => l.split("|").map((x) => x.trim())).filter(([l, u]) => l && u && /^https?:\/\//.test(u)).map(([l, u], i) => (
              <a key={i} href={u} target="_blank" rel="noreferrer" style={{ padding: "10px 18px", borderRadius: 24, background: p.accent, color: "#fff", textDecoration: "none", fontWeight: 600 }}>{l}</a>
            ))}
          </div>
        );
        case "html": return <iframe title="Eigener Inhalt" sandbox="allow-scripts" srcDoc={String(pr.code ?? "")} style={{ width: "100%", height: Number(pr.height) || 300, border: 0, borderRadius: 12, marginBottom: 14, background: "transparent" }} />;
        case "spacer": return <div style={{ height: Number(pr.height) || 24 }} />;
        case "divider": return <hr style={{ border: 0, borderTop: "1px solid rgba(255,255,255,.2)", margin: "18px 0" }} />;
        case "sisters": return info.sisters?.length ? <div style={{ marginBottom: 14 }}>{pr.heading || "Auch hören"}: {info.sisters.map((x: any) => <a key={x.slug} href={`/player/${x.slug}`} style={{ color: p.accent, marginRight: 10 }}>{x.name}</a>)}</div> : null;
        default: return null;
      }
    };
    return (
      <div style={css} className="rc-public">
        {p.custom_css && <style>{p.custom_css}</style>}
        <div style={{ maxWidth: 820, margin: "0 auto", padding: "24px 16px" }}>
          {blocks.map((b) => <div key={b.id} className={`rc-block rc-${b.type}`}>{block(b)}</div>)}
          <audio ref={audio} onError={() => setPlaying(false)} />
        </div>
      </div>
    );
  }

  return (
    <div style={css}>
      <div style={{ maxWidth: 760, margin: "0 auto", padding: widget ? 12 : "32px 16px" }}>
        {!widget && <><h1 style={{ margin: 0 }}>{p.title || info.station.name}</h1><div style={{ opacity: 0.75, marginBottom: 20 }}>{p.tagline}</div></>}
        <div style={{ ...box, display: "flex", alignItems: "center", gap: 16 }}>
          {playBtn}
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 1, color: now?.on_air === "live" ? "#ff8a8a" : p.accent }}>{now?.on_air === "live" ? "● Live" : "Jetzt läuft"}</div>
            <div style={{ fontSize: 20, fontWeight: 700 }}>{title}</div>
            {now?.next?.length > 0 && <div style={{ opacity: 0.7, fontSize: 13 }}>Danach: {now.next[0].artist ? `${now.next[0].artist} – ` : ""}{now.next[0].title}</div>}
          </div>
        </div>
        <audio ref={audio} onError={() => setPlaying(false)} />
        {info.sisters?.length > 0 && <div style={{ marginBottom: 14 }}>Auch hören: {info.sisters.map((s: any) => <a key={s.slug} href={`/player/${s.slug}`} style={{ color: p.accent, marginRight: 10 }}>{s.name}</a>)}</div>}
        {p.sections.includes("history") && history && (
          <div style={box}><h3 style={{ marginTop: 0 }}>Zuletzt gespielt</h3>{history.map((h, i) => <div key={i} style={{ padding: "3px 0" }}><span style={{ opacity: 0.6 }}>{new Date(h.started_at).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}</span> {h.artist} – {h.title}</div>)}</div>
        )}
        {p.sections.includes("schedule") && scheduleBox}
        {p.sections.includes("requests") && p.requests_enabled && requestsForm}
        {p.sections.includes("requests") && greetings && greetings.length > 0 && (
          <div style={box}><h3 style={{ marginTop: 0 }}>Grüße</h3>{greetings.map((g, i) => <div key={i} style={{ padding: "4px 0" }}><b>{g.name || "Jemand"}</b>: {g.message}{g.song ? ` 🎵 ${g.song}` : ""}</div>)}</div>
        )}
        {p.sections.includes("about") && p.about && <div style={box}>{p.about}</div>}
      </div>
    </div>
  );
}
